import {CommonModule} from '@angular/common';
import {HttpErrorResponse} from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  signal,
  inject,
} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {MatDialog} from '@angular/material/dialog';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {Store} from '@ngrx/store';
import {BehaviorSubject, Observable} from 'rxjs';
import {CurrentSkedTask, Subtask, SubtaskNote} from 'src/app/core/model/task';
import {User} from 'src/app/core/model/user';
import {DeleteImageAction} from 'src/app/core/store/active-user/active-user.action';
import {AddSubtaskNoteAction} from 'src/app/core/store/current-sked/current-sked.action';
import {selectUsersMap} from 'src/app/core/store/users/users.selector';
import {FileUploadService} from 'src/app/services/file-upload.service';
import {MessageService} from 'src/app/services/message.service';
import {OphButtonModule} from 'src/app/shared/design/oph-button/oph-button.module';
import {OphIconModule} from 'src/app/shared/design/oph-icon/oph-icon.module';
import {OphInputOrangeComponent} from 'src/app/shared/design/oph-inputs/oph-input-orange/oph-input-orange.component';
import {OphXButtonComponent} from 'src/app/shared/design/oph-x-button/oph-x-button.component';
import {CurrentSkedTaskDetailSubtaskImageDialogComponent} from '../subtask-image-dialog/current-sked-task-detail-subtask-image-dialog.component';
import {PipesModule} from 'src/app/shared/pipes/pipes.module';
import {MobileService} from 'src/app/core/page/mobile.service';

@Component({
  selector: 'current-sked-notes',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    OphXButtonComponent,
    OphIconModule,
    OphInputOrangeComponent,
    OphButtonModule,
    MatProgressSpinnerModule,
    PipesModule,
  ],
  templateUrl: './current-sked-notes.component.html',
  styleUrl: './current-sked-notes.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CurrentSkedNotesComponent implements OnDestroy, OnChanges, OnInit {
  private readonly mobileService = inject(MobileService);
  @Input() subtask: Subtask;
  @Input() task: CurrentSkedTask;
  @Input() uploadImage: boolean;
  @Output() updateSubtask = new EventEmitter<CurrentSkedTask>();
  @Output() closeNotes = new EventEmitter<void>();

  @ViewChild('fileUpload') fileUpload: ElementRef;

  reversedNotes = signal<SubtaskNote[]>([]);
  tempImageUrls: {[key: number]: string} = {};
  noteForm = new FormGroup({
    taskId: new FormControl(''),
    subtaskId: new FormControl(''),
    trackerId: new FormControl(''),
    images: new FormControl<string[]>([]),
    note: new FormControl(''),
  });

  uploadProgress$ = new BehaviorSubject<{[key: number]: number}>({});
  imageLoading$ = new BehaviorSubject<{[key: number]: boolean}>({});
  notePostLoading$ = new BehaviorSubject<boolean>(false);

  usersMap$: Observable<Record<string, User>> = this.store$.select(selectUsersMap);

  constructor(
    private store$: Store,
    private fileUploadService: FileUploadService,
    private messageService: MessageService,
    private dialog: MatDialog
  ) {}

  ngOnInit() {
    if (this.uploadImage) {
      this.onUploadPhotoClick();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.task || changes.subtask) {
      if (this.task && this.subtask) {
        this.setupSubtaskNoteForm(this.task, this.subtask);
        this.reversedNotes.set([...(this.subtask.notes || [])].reverse());
      }
    }
  }

  ngOnDestroy() {
    // Clean up any remaining temp URLs
    Object.keys(this.tempImageUrls).forEach(key => {
      URL.revokeObjectURL(this.tempImageUrls[key]);
    });
  }

  private clearLoadingStates(index: number) {
    const currentLoadingState = {...this.imageLoading$.value};
    const currentProgress = {...this.uploadProgress$.value};
    delete currentLoadingState[index];
    delete currentProgress[index];

    this.imageLoading$.next(currentLoadingState);
    this.uploadProgress$.next(currentProgress);
  }

  setupSubtaskNoteForm(task: CurrentSkedTask, subtask: Subtask) {
    this.noteForm.patchValue({
      taskId: task._id,
      subtaskId: subtask._id || '',
      trackerId: subtask.trackerId || null,
      images: [],
      note: '',
    });
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      const validationError = this.fileUploadService.validateFile(file);
      if (validationError) {
        this.messageService.add(validationError);
        input.value = ''; // Reset the input after validation error
        return;
      }

      const currentImages = this.noteForm.get('images').value || [];
      const newImageIndex = currentImages.length;

      // Create temporary preview URL
      this.tempImageUrls[newImageIndex] = URL.createObjectURL(file);

      this.noteForm.patchValue({
        images: [...currentImages, ''],
      });

      this.imageLoading$.next({
        ...this.imageLoading$.value,
        [newImageIndex]: true,
      });

      this.fileUploadService.uploadFile(file).subscribe({
        next: result => {
          if ('progress' in result && !('url' in result)) {
            // Progress event
            this.uploadProgress$.next({
              ...this.uploadProgress$.value,
              [newImageIndex]: result.progress,
            });
          } else if ('url' in result) {
            // Complete event with URL
            this.uploadProgress$.next({
              ...this.uploadProgress$.value,
              [newImageIndex]: 100,
            });

            // Update the form with the final URL
            this.noteForm.patchValue({
              images: [...currentImages, result.url],
            });

            // Preload the actual image before removing the preview
            const img = new Image();
            img.onload = () => {
              // Only remove the preview once the actual image is loaded
              URL.revokeObjectURL(this.tempImageUrls[newImageIndex]);
              delete this.tempImageUrls[newImageIndex];
            };
            img.src = result.url;

            this.clearLoadingStates(newImageIndex);
          }
        },
        error: (error: HttpErrorResponse) => {
          this.messageService.add(error.message || 'There was a problem uploading the image.');
          this.clearLoadingStates(newImageIndex);
          // Clean up temp URL on error
          URL.revokeObjectURL(this.tempImageUrls[newImageIndex]);
          delete this.tempImageUrls[newImageIndex];
        },
        complete: () => {
          // Reset the input value after the upload is complete
          input.value = '';
        },
      });
    }
  }

  onDeleteImage(index: number) {
    const images = [...this.noteForm.get('images').value];
    const imageToDelete = images[index];
    images.splice(index, 1);
    this.noteForm.patchValue({images});

    // Revoke temporary URL if it exists
    if (this.tempImageUrls[index]) {
      URL.revokeObjectURL(this.tempImageUrls[index]);
      delete this.tempImageUrls[index];
    }

    // Only dispatch delete action if we have a valid image URL to delete
    if (imageToDelete) {
      this.store$.dispatch(
        new DeleteImageAction({
          ids: encodeURIComponent(imageToDelete),
        })
      );
    }
  }

  onUploadPhotoClick() {
    this.fileUpload.nativeElement.click();
  }

  onPost() {
    this.notePostLoading$.next(true);
    this.store$.dispatch(
      new AddSubtaskNoteAction({
        subtaskNote: this.noteForm.value as SubtaskNote,
        onSuccess: (task: CurrentSkedTask) => this.onPostSuccess(task),
        onFailure: () => this.onPostFailure(),
      })
    );
  }

  onPostSuccess(task: CurrentSkedTask) {
    this.noteForm.patchValue({
      images: [],
      note: '',
    });
    this.updateSubtask.emit(task);
    this.notePostLoading$.next(false);
    // Reset any remaining temp URLs
    Object.keys(this.tempImageUrls).forEach(key => {
      URL.revokeObjectURL(this.tempImageUrls[key]);
      delete this.tempImageUrls[key];
    });
    // Reset loading and progress states
    this.imageLoading$.next({});
    this.uploadProgress$.next({});
  }

  onPostFailure() {
    this.notePostLoading$.next(false);
  }

  hasLoadingImages(imageLoading: {[key: number]: boolean}): boolean {
    return Object.values(imageLoading).some(isLoading => isLoading);
  }

  openImageDialog(note: SubtaskNote) {
    this.dialog.open(CurrentSkedTaskDetailSubtaskImageDialogComponent, {
      autoFocus: this.mobileService.isMobilePlatform() ? false : undefined,
      data: note,
      width: '100%',
      maxWidth: '720px',
      height: '100%',
      maxHeight: '615px',
    });
  }
}
